import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const OrderTable = ({ 
  orders, 
  selectedOrders, 
  onOrderSelect, 
  onSelectAll, 
  onBulkAction,
  refreshTrigger,
  onStatusUpdate, 
  onBulkUpdate,
  onEditOrder,
  onDeleteOrder,
  loading = false
}) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [carHouseOptions, setCarHouseOptions] = useState([]);
  const navigate = useNavigate();

  // Use orders prop as the primary data source
  const displayOrders = orders || [];

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setEditFormData({
      license_plate: order.license_plate || '',
      status: order.status || 'pending',
      priority: order.priority || 'normal',
      total_amount: order.total_amount || 0,
      description: order.description || '',
      color: order.color || '',
      service_type_id: order.service_type_id || '',
      car_house_id: order.car_house_id || '',
      return_date: order.return_date || '',
      return_time: order.return_time || '',
      model: order.vehicle?.model || '',
      year: order.vehicle?.year || ''
    });
    setIsEditModalOpen(true);
    loadServiceTypes();
    loadCarHouses();
  };

  const handleSaveEdit = async () => {
    if (!editingOrder) return;
    
    setIsSaving(true);
    try {
      // Separate order fields from vehicle fields
      const { model, year, ...orderFields } = editFormData;
      
      // Clean order fields - convert empty strings to null for date/time fields
      const cleanedOrderFields = {
        ...orderFields,
        return_date: orderFields.return_date && orderFields.return_date.trim() !== '' ? orderFields.return_date : null,
        return_time: orderFields.return_time && orderFields.return_time.trim() !== '' ? orderFields.return_time : null,
        // Also clean other fields that might be empty strings
        license_plate: orderFields.license_plate && orderFields.license_plate.trim() !== '' ? orderFields.license_plate : null,
        color: orderFields.color && orderFields.color.trim() !== '' ? orderFields.color : null,
        description: orderFields.description && orderFields.description.trim() !== '' ? orderFields.description : null
      };
      
      // Update the order (only order-related fields)
      const { error: orderError } = await supabase
        .from('orders')
        .update(cleanedOrderFields)
        .eq('id', editingOrder.id)
        .select()
        .single();
      
      if (orderError) {
        console.error('Error updating order:', orderError);
        return;
      }

      // Update the vehicle if model or year changed
      if (editingOrder.vehicle?.id && (model || year)) {
        const vehicleUpdates = {};
        if (model && model.trim() !== '') vehicleUpdates.model = model.trim();
        if (year && year.toString().trim() !== '') vehicleUpdates.year = parseInt(year);
        
        if (Object.keys(vehicleUpdates).length > 0) {
          const { error: vehicleError } = await supabase
            .from('vehicles')
            .update(vehicleUpdates)
            .eq('id', editingOrder.vehicle.id);
          
          if (vehicleError) {
            console.error('Error updating vehicle:', vehicleError);
            // Don't return here - order was updated successfully
          }
        }
      }

      // Call parent callback if provided
      onEditOrder?.(editingOrder.id, editFormData);
      
      // Close modal and reset state
      setIsEditModalOpen(false);
      setEditingOrder(null);
      setEditFormData({});
    } catch (error) {
      console.error('Error updating order:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  const handleMarkAsCompleted = async (order) => {
    if (!order?.id) return;
    
    try {
      // Update the order status to completed
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', order.id);
      
      if (error) {
        console.error('Error updating order status:', error);
        alert('Fehler beim Aktualisieren des Auftragsstatus: ' + error.message);
        return;
      }

      // Call parent callback if provided
      onStatusUpdate?.(order.id, 'completed');
      
      // Close modal and show success message
      setIsDetailsModalOpen(false);
      setSelectedOrder(null);
      alert('Auftrag erfolgreich als "Erledigt" markiert!');
      
    } catch (error) {
      console.error('Error marking order as completed:', error);
      alert('Fehler beim Markieren des Auftrags als erledigt: ' + error.message);
    }
  };

  const loadServiceTypes = async () => {
    try {
      // Import serviceTypeService dynamically to avoid circular dependencies
      const { serviceTypeService } = await import('../../../services/serviceTypeService.js');
      const { data, error } = await serviceTypeService.getServiceTypeOptions();
      if (error) {
        console.error('Error loading service types:', error);
        setServiceTypeOptions([]);
      } else {
        setServiceTypeOptions(data || []);
      }
    } catch (err) {
      console.error('Error loading service types:', err);
      setServiceTypeOptions([]);
    }
  };

  const loadCarHouses = async () => {
    try {
      const { data, error } = await supabase
        .from('car_houses')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) {
        console.error('Error loading car houses:', error);
        setCarHouseOptions([]);
      } else {
        const options = (data || []).map(h => ({ value: h.id, label: h.name }));
        setCarHouseOptions(options);
      }
    } catch (err) {
      console.error('Error loading car houses:', err);
      setCarHouseOptions([]);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      // Prepare update data
      const updateData = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      // If status is being changed to 'pending' (Neu) or 'review' (In Prüfung), unassign technician
      if (newStatus === 'pending' || newStatus === 'review') {
        updateData.assigned_technician_id = null;
      }

      
      // First, let's check the current state of the order
      const { data: currentOrder, error: currentError } = await supabase
        .from('orders')
        .select('id, status, assigned_technician_id')
        .eq('id', orderId)
        .single();
      
      if (currentError) {
        console.error('Error fetching current order:', currentError);
      } else {
      }
      
      // Use a more explicit update approach
      let query = supabase.from('orders').update(updateData).eq('id', orderId);
      
      // For debugging, let's also log the SQL that would be generated
      
      const { error } = await query;
      
      if (error) {
        console.error('Error updating order status:', error);
        console.error('Error details:', error);
        return;
      }

      
      // Verify the update by fetching the updated order
      const { data: updatedOrder, error: fetchError } = await supabase
        .from('orders')
        .select('id, status, assigned_technician_id')
        .eq('id', orderId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching updated order:', fetchError);
      } else {
        
        // Also check the raw database value
        const { data: rawOrder, error: rawError } = await supabase
          .from('orders')
          .select('id, status, assigned_technician_id')
          .eq('id', orderId)
          .single();
        
        if (!rawError) {
        }
      }

      // Call parent callback if provided
      onStatusUpdate?.(orderId, newStatus);
      
      // Show success message with technician unassignment info if applicable
      if (newStatus === 'pending' || newStatus === 'review') {
        alert(`Status erfolgreich zu "${newStatus === 'pending' ? 'Neu' : 'In Prüfung'}" geändert und Techniker abgemeldet!`);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handleBulkStatusUpdate = async (selectedIds, newStatus) => {
    try {
      // Prepare update data
      const updateData = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      // If status is being changed to 'pending' (Neu) or 'review' (In Prüfung), unassign technician
      if (newStatus === 'pending' || newStatus === 'review') {
        updateData.assigned_technician_id = null;
      }

      // Update multiple orders
      await Promise.all(
        selectedIds?.map(id => 
          supabase
            .from('orders')
            .update(updateData)
            .eq('id', id)
        ) || []
      );

      // Call parent callback if provided
      onBulkUpdate?.(selectedIds, newStatus);
      
      // Show success message with technician unassignment info if applicable
      if (newStatus === 'pending' || newStatus === 'review') {
        alert(`Status für ${selectedIds?.length || 0} Aufträge erfolgreich zu "${newStatus === 'pending' ? 'Neu' : 'In Prüfung'}" geändert und Techniker abgemeldet!`);
      }
    } catch (error) {
      console.error('Error updating bulk order status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Neu', className: 'bg-blue-100 text-blue-800' },
      'review': { label: 'In Prüfung', className: 'bg-yellow-100 text-yellow-800' },
      'assigned': { label: 'Zugewiesen', className: 'bg-purple-100 text-purple-800' },
      'in_progress': { label: 'In Bearbeitung', className: 'bg-orange-100 text-orange-800' },
      'completed': { label: 'Erledigt', className: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'Storniert', className: 'bg-red-100 text-red-800' },
      'on_hold': { label: 'Pausiert', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig?.[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'low': { label: 'Niedrig', className: 'text-gray-600' },
      'normal': { label: 'Normal', className: 'text-blue-600' },
      'high': { label: 'Hoch', className: 'text-orange-600' },
      'urgent': { label: 'Dringend', className: 'text-red-600' }
    };

    const config = priorityConfig?.[priority] || { label: priority, className: 'text-gray-600' };
    
    return (
      <span className={`text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Removed handleRowClick to make orders unclickable
  // const handleRowClick = (orderId) => {
  //   // Navigate to order details
  //   navigate(`/order-details/${orderId}`);
  // };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-muted transition-colors duration-150"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? "ChevronUp" : "ChevronDown"} 
            size={14} 
          />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }



  return (
    <>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left">
                  <Checkbox
                    checked={selectedOrders?.length === displayOrders?.length && displayOrders?.length > 0}
                    onChange={(e) => onSelectAll?.(e?.target?.checked)}
                    indeterminate={selectedOrders?.length > 0 && selectedOrders?.length < displayOrders?.length}
                  />
                </th>
                <SortableHeader field="order_number">Auftragsnr.</SortableHeader>
                <SortableHeader field="vin">VIN</SortableHeader>
                <SortableHeader field="license_plate">Kennzeichen</SortableHeader>
                <SortableHeader field="car_house">Autohaus</SortableHeader>
                <SortableHeader field="service_type">Service-Typ</SortableHeader>
                <SortableHeader field="effective_price">Preis</SortableHeader>
                <SortableHeader field="status">Status</SortableHeader>
                <SortableHeader field="priority">Priorität</SortableHeader>
                <SortableHeader field="assigned_technician">Techniker</SortableHeader>
                <SortableHeader field="return_date">Rückgabe</SortableHeader>
                <SortableHeader field="created_at">Erstellt</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {displayOrders?.map((order) => (
                <tr 
                  key={order?.id} 
                  className="hover:bg-muted transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e?.stopPropagation()}>
                    <Checkbox
                      checked={selectedOrders?.includes(order?.id)}
                      onChange={(e) => onOrderSelect?.(order?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                    {order?.order_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary font-mono">
                    {order?.vin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary font-mono">
                    {order?.license_plate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {order?.car_house?.name || order?.car_house_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {order?.service_type?.display_name || order?.service_type?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary font-medium">
                    {order?.effective_price !== undefined && order?.effective_price !== null
                      ? `€ ${parseFloat(order.effective_price).toFixed(2)}`
                      : (order?.total_amount ? `€ ${parseFloat(order.total_amount).toFixed(2)}` : '-')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(order?.status)}
                    
                    {/* <div className="mt-1">
                      <select
                        className="text-xs border rounded px-1 py-0.5"
                        value={order?.status}
                        onChange={(e) => handleStatusUpdate(order?.id, e.target.value)}
                      >
                        <option value="pending">Neu</option>
                        <option value="review">In Prüfung</option>
                        <option value="assigned">Zugewiesen</option>
                        <option value="in_progress">In Bearbeitung</option>
                        <option value="completed">Abgeschlossen</option>
                        <option value="cancelled">Storniert</option>
                        <option value="on_hold">Pausiert</option>
                      </select>
                    </div> */}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPriorityBadge(order?.priority)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {order?.assigned_technician ? 
                  `${order.assigned_technician.first_name || ''} ${order.assigned_technician.last_name || ''}`.trim() || 'Nicht zugewiesen' 
                  : 'Nicht zugewiesen'
                }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {order?.return_date ? (
                      <div>
                        <div className="font-medium">{new Date(order.return_date).toLocaleDateString('de-DE')}</div>
                        {order?.return_time && (
                          <div className="text-xs text-muted-foreground">
                            {order.return_time}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {formatDate(order?.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e?.stopPropagation()}>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(order)}
                        title="Details anzeigen"
                      >
                        <Icon name="Eye" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditOrder(order)}
                        title="Bearbeiten"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteOrder?.(order)}
                        title="Löschen"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                      {(!order?.assigned_technician_id || ['pending', 'review'].includes(order?.status)) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onBulkAction?.('assign', [order?.id])}
                          title="Techniker zuweisen"
                        >
                          <Icon name="UserPlus" size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4 p-4">
          {displayOrders?.map((order) => (
            <div 
              key={order?.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow duration-150"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={selectedOrders?.includes(order?.id)}
                    onChange={(e) => {
                      e?.stopPropagation();
                      onOrderSelect?.(order?.id, e?.target?.checked);
                    }}
                  />
                  <div>
                    <div className="font-medium text-text-primary">{order?.order_number}</div>
                    <div className="text-sm text-text-secondary font-mono">{order?.vin}</div>
                    <div className="text-sm text-text-secondary font-mono">{order?.license_plate}</div>
                  </div>
                </div>
                {getStatusBadge(order?.status)}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Autohaus:</span>
                  <span className="text-text-primary">{order?.car_house?.name || order?.car_house_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Service-Typ:</span>
                  <span className="text-text-primary">{order?.service_type?.display_name || order?.service_type?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Preis:</span>
                  <span className="text-text-primary font-medium">
                    {order?.effective_price !== undefined && order?.effective_price !== null
                      ? `€ ${parseFloat(order.effective_price).toFixed(2)}`
                      : (order?.total_amount ? `€ ${parseFloat(order.total_amount).toFixed(2)}` : '-')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Priorität:</span>
                  {getPriorityBadge(order?.priority)}
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Techniker:</span>
                                      <span className="text-text-primary">
                      {order?.assigned_technician ? 
                        `${order.assigned_technician.first_name || ''} ${order.assigned_technician.last_name || ''}`.trim() || 'Nicht zugewiesen' 
                        : 'Nicht zugewiesen'
                      }
                    </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Erstellt:</span>
                  <span className="text-text-primary">{formatDate(order?.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Rückgabe:</span>
                  <span className="text-text-primary">
                    {order?.return_date ? (
                      <div>
                        <div>{new Date(order.return_date).toLocaleDateString('de-DE')}</div>
                        {order?.return_time && (
                          <div className="text-xs text-muted-foreground">{order.return_time}</div>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleViewDetails(order);
                  }}
                >
                  <Icon name="Eye" size={16} className="mr-1" />
                  Details
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleEditOrder(order);
                  }}
                >
                  <Icon name="Edit" size={16} className="mr-1" />
                  Bearbeiten
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onDeleteOrder?.(order);
                  }}
                >
                  <Icon name="Trash2" size={16} className="mr-1" />
                  Löschen
                </Button>
                {(!order?.assigned_technician_id || ['pending', 'review'].includes(order?.status)) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onBulkAction?.('assign', [order?.id]);
                    }}
                  >
                    <Icon name="UserPlus" size={16} className="mr-1" />
                    Zuweisen
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {displayOrders?.length === 0 && !loading && (
          <div className="text-center py-12">
            <Icon name="ClipboardList" size={48} className="mx-auto text-text-secondary mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">Keine Aufträge gefunden</h3>
            <p className="text-text-secondary">Passen Sie Ihre Filter an oder erstellen Sie einen neuen Auftrag.</p>
          </div>
        )}
      </div>

      {/* Edit Order Modal */}
      {isEditModalOpen && editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Edit" size={20} color="white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Auftrag bearbeiten
                  </h2>
                  <p className="text-sm text-gray-500">
                    Auftragsnr. {editingOrder.order_number}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingOrder(null);
                  setEditFormData({});
                }}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

                         {/* Content */}
             <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kennzeichen
                  </label>
                  <input
                    type="text"
                    value={editFormData.license_plate}
                    onChange={(e) => handleInputChange('license_plate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Kennzeichen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Autohaus
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Autohaus auswählen' },
                      ...carHouseOptions
                    ]}
                    value={editFormData.car_house_id}
                    onChange={(value) => handleInputChange('car_house_id', value)}
                    placeholder="Autohaus auswählen"
                    searchable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preis
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.total_amount}
                    onChange={(e) => handleInputChange('total_amount', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <Select
                    options={[
                      { value: 'pending', label: 'Neu' },
                      { value: 'review', label: 'In Prüfung' },
                      { value: 'assigned', label: 'Zugewiesen' },
                      { value: 'in_progress', label: 'In Bearbeitung' },
                      { value: 'completed', label: 'Erledigt' },
                      { value: 'cancelled', label: 'Storniert' },
                      { value: 'on_hold', label: 'Pausiert' }
                    ]}
                    value={editFormData.status}
                    onChange={(value) => handleInputChange('status', value)}
                    placeholder="Status auswählen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service-Typ
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Service-Typ auswählen' },
                      ...serviceTypeOptions
                    ]}
                    value={editFormData.service_type_id}
                    onChange={(value) => handleInputChange('service_type_id', value)}
                    placeholder="Service-Typ auswählen"
                    searchable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priorität
                  </label>
                  <Select
                    options={[
                      { value: 'low', label: 'Niedrig' },
                      { value: 'normal', label: 'Normal' },
                      { value: 'high', label: 'Hoch' },
                      { value: 'urgent', label: 'Dringend' }
                    ]}
                    value={editFormData.priority}
                    onChange={(value) => handleInputChange('priority', value)}
                    placeholder="Priorität auswählen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Farbe
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Farbe auswählen' },
                      { value: 'blau', label: 'Blau' },
                      { value: 'grün', label: 'Grün' },
                      { value: 'schwarz', label: 'Schwarz' },
                      { value: 'weiß', label: 'Weiß' },
                      { value: 'rot', label: 'Rot' },
                      { value: 'gelb', label: 'Gelb' },
                      { value: 'gold', label: 'Gold' },
                      { value: 'grau', label: 'Grau' },
                      { value: 'braun', label: 'Braun' },
                      { value: 'dunkelblau', label: 'Dunkelblau' }
                    ]}
                    value={editFormData.color}
                    onChange={(value) => handleInputChange('color', value)}
                    placeholder="Farbe auswählen"
                  />
                </div>

                {/* Vehicle Model and Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modell
                  </label>
                  <input
                    type="text"
                    value={editFormData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Fahrzeugmodell"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Baujahr
                  </label>
                  <input
                    type="number"
                    value={editFormData.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Baujahr"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beschreibung
                </label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optionale Beschreibung des Auftrags..."
                />
              </div>

              {/* Return Date and Time */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Rückgabe</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Rückgabedatum
                    </label>
                    <input
                      type="date"
                      value={editFormData.return_date}
                      onChange={(e) => handleInputChange('return_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Rückgabezeit
                    </label>
                    <input
                      type="time"
                      value={editFormData.return_time}
                      onChange={(e) => handleInputChange('return_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingOrder(null);
                  setEditFormData({});
                }}
              >
                Abbrechen
              </Button>
              <Button
                variant="default"
                onClick={handleSaveEdit}
                loading={isSaving}
              >
                <Icon name="Save" size={16} className="mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {isDetailsModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} color="white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Auftragsdetails
                  </h2>
                  <p className="text-sm text-gray-500">
                    Auftragsnr. {selectedOrder.order_number}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedOrder(null);
                }}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Order Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Auftragsinformationen</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Auftragsnummer:</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedOrder.order_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Status:</span>
                        <span className="text-sm text-gray-900">{getStatusBadge(selectedOrder.status)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Priorität:</span>
                        <span className="text-sm text-gray-900">{getPriorityBadge(selectedOrder.priority)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Erstellt am:</span>
                        <span className="text-sm text-gray-900">{formatDate(selectedOrder.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Gesamtpreis:</span>
                        <span className="text-sm text-gray-900 font-medium">
                          {selectedOrder.total_amount ? `€ ${parseFloat(selectedOrder.total_amount).toFixed(2)}` : '-'}
                        </span>
                      </div>
                      {selectedOrder.description && (
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">Beschreibung:</span>
                          <span className="text-sm text-gray-900 max-w-xs text-right">{selectedOrder.description}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Autohaus Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Autohaus-Informationen</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Autohaus:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.car_house?.name || selectedOrder.car_house_id || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Code:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.car_house?.code || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Stadt:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.car_house?.city || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Fahrzeuginformationen</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">VIN:</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedOrder.vin || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Kennzeichen:</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedOrder.license_plate || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Marke/Modell:</span>
                        <span className="text-sm text-gray-900">
                          {selectedOrder.vehicle?.make} {selectedOrder.vehicle?.model}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Baujahr:</span>
                        <span className="text-sm text-gray-900">{selectedOrder.vehicle?.year || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Farbe:</span>
                        <span className="text-sm text-gray-900 capitalize">{selectedOrder.color || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Service Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Service-Informationen</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Service-Typ:</span>
                        <span className="text-sm text-gray-900">
                          {selectedOrder.service_type?.display_name || selectedOrder.service_type?.name || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Autohaus:</span>
                        <span className="text-sm text-gray-900">
                          {selectedOrder.car_house?.name || selectedOrder.car_house_id || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600">Zugewiesener Techniker:</span>
                        <span className="text-sm text-gray-900">
                          {selectedOrder.assigned_technician ? 
                      `${selectedOrder.assigned_technician.first_name || ''} ${selectedOrder.assigned_technician.last_name || ''}`.trim() || 'Nicht zugewiesen' 
                      : 'Nicht zugewiesen'
                    }
                        </span>
                      </div>
                      {selectedOrder.return_date && (
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">Rückgabedatum:</span>
                          <span className="text-sm text-gray-900">
                            {new Date(selectedOrder.return_date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      )}
                      {selectedOrder.return_time && (
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">Rückgabezeit:</span>
                          <span className="text-sm text-gray-900">{selectedOrder.return_time}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {(selectedOrder.work_notes || selectedOrder.internal_notes) && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Zusätzliche Notizen</h3>
                  <div className="space-y-4">
                    {selectedOrder.work_notes && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">Arbeitsnotizen:</h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedOrder.work_notes}</p>
                      </div>
                    )}
                    {selectedOrder.internal_notes && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">Interne Notizen:</h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedOrder.internal_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedOrder(null);
                }}
              >
                Schließen
              </Button>
              
              {/* Mark as Completed Button - only show if not already completed */}
              {selectedOrder?.status !== 'completed' && (
                <Button
                  variant="default"
                  onClick={() => handleMarkAsCompleted(selectedOrder)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Icon name="CheckCircle" size={16} className="mr-2" />
                  Als Erledigt markieren
                </Button>
              )}
              
              <Button
                variant="default"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedOrder(null);
                  handleEditOrder(selectedOrder);
                }}
              >
                <Icon name="Edit" size={16} className="mr-2" />
                Bearbeiten
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderTable;